import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { combineLatest, of, Subscription, switchMap } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { GlobalPropertiesContext } from '../shared/contexts/global-properties.context';
import { CustomUiOptionsContext } from '../shared/contexts/custom-ui-options.context';
import { MicrosoftDialPlansFieldConfigContext } from '../shared/contexts/microsoft-dial-plans-field-config.context';
import { SystemStatusContext } from '../shared/contexts/system-status.context';
import { MicrosoftTenantsStateContext } from '../shared/contexts/microsoft-tenants-state.context';
import { map, take, tap } from 'rxjs/operators';
import { CurrentUser, PrivilegedTenant, ZpmPrivilege } from '../shared/models/generated/ziroModels';
import { NavProps, NavSection, NavSectionLink } from '../shared/side-navigation/side-navigation.model';
import { ZiroIcons } from '../shared/models/ziro-icons.enum';
import { AuthenticationContext } from '../shared/contexts/authentication.context';

const ZPC_REPORT_PATHS: { path: string; tkey: string; dataAutomation: string; icon: ZiroIcons }[] = [
  {
    path: 'dashboard',
    tkey: 'tkey;menu.report.dashboard',
    dataAutomation: 'menu-dashboard-link',
    icon: ZiroIcons.TABLE,
  },
  {
    path: 'orphaned-devices',
    tkey: 'tkey;menu.admin.admin.orphaned_devices',
    dataAutomation: 'menu-orphaned-devices-link',
    icon: ZiroIcons.NO_DEVICES_SMALL,
  },
  {
    path: 'unused-phone-button-templates',
    tkey: 'tkey;menu.report.unused_phone_button_template.title',
    dataAutomation: 'menu-unused-phone-button-template-link',
    icon: ZiroIcons.DESKPHONE_SMALL,
  },
  {
    path: 'dn-availability',
    tkey: 'tkey;menu.report.dn_availability',
    dataAutomation: 'menu-directory-number-availability-link',
    icon: ZiroIcons.PHONE,
  },
  {
    path: 'device-utilization',
    tkey: 'tkey;menu.report.device_utilization',
    dataAutomation: 'menu-device-utilization-link',
    icon: ZiroIcons.MIXED_DEVICES_SMALL,
  },
  {
    path: 'cucm-license-optimization',
    tkey: 'tkey;reports.cucm_license_optimizer.overview.title',
    dataAutomation: 'menu-cucm-license-optimization-link',
    icon: ZiroIcons.BILLING,
  },
  {
    path: 'global-directory',
    tkey: 'tkey;reports.global_directory.title',
    dataAutomation: 'menu-global-directory-link',
    icon: ZiroIcons.USER,
  },
  {
    path: 'bulk-line-appearance-management',
    tkey: 'tkey;reports.bulk_line_appearance_management.title',
    dataAutomation: 'menu-line-appearance-management-link',
    icon: ZiroIcons.PHONE_LIST,
  },
  {
    path: 'call-logs',
    tkey: 'tkey;reports.call_logs.title',
    dataAutomation: 'menu-call-logs-link',
    icon: ZiroIcons.PHONE,
  },
  {
    path: 'custom-reports',
    tkey: 'tkey;reports.custom_reports.title',
    dataAutomation: 'menu-custom-reports-link',
    icon: ZiroIcons.TABLE,
  },
];

function isHostedDialPlanPath(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  return /^tenants\/\d+\/dial-plan-inventory$/.test(url);
}

function onPremLinkDisabled(relativePath: string): (flags: NavProps) => boolean {
  return (flags) => flags.endpointDisabled && relativePath !== 'custom-reports';
}

function onPremLinkTooltip(relativePath: string): (flags: NavProps) => string | null {
  return (flags) => {
    if (flags.endpointDisabled && relativePath !== 'custom-reports') {
      return 'tkey;reports.side_nav.disabled_server.tooltip';
    }
    return null;
  };
}

function hostedLinkDisabled(path: string): (flags: NavProps) => boolean {
  return (flags) => !flags.isMsDialPlanConfigured && isHostedDialPlanPath(path);
}

function hostedDialPlanTooltipFor(path: string): (flags: NavProps) => string | null {
  return (flags) => {
    if (!flags.isMsDialPlanConfigured && isHostedDialPlanPath(path)) {
      return 'tkey;global.zpm.no_dial_plans.text';
    }
    return null;
  };
}

function buildZpcReportLinks(): NavSectionLink[] {
  return ZPC_REPORT_PATHS.map(
    (row): NavSectionLink => ({
      link: row.path,
      text: row.tkey,
      dataAutomation: row.dataAutomation,
      icon: row.icon,
      isRouterLink: true,
      isDisabled: onPremLinkDisabled(row.path),
      disabledTooltip: onPremLinkTooltip(row.path),
    }),
  );
}

@Component({
  selector: 'ziro-reporting',
  templateUrl: './reporting.component.html',
  styleUrls: ['../admin/admin-page.scss'],
  standalone: false,
})
export class ReportingComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _globalPropertiesContext = inject(GlobalPropertiesContext);
  private _customUiOptionsContext = inject(CustomUiOptionsContext);
  private _microsoftDialPlansFieldConfigContext = inject(MicrosoftDialPlansFieldConfigContext);
  private _systemStatusContext = inject(SystemStatusContext);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _authenticationContext = inject(AuthenticationContext);

  pageDisabled = false;
  pageDisabledMessage = 'tkey;reports.page_disabled.unlicensed';
  isDialPlansConfigured = false;
  cachedTenantId: number;
  ziroIcons = ZiroIcons;

  navProps = signal<NavProps>({
    endpointDisabled: false,
    isMsDialPlanConfigured: true,
  });
  navSections = signal<NavSection[]>([]);

  private _isHosted = false;
  private _reportingEnabled = false;
  private _endpointDisabled = false;
  private _currentUser: CurrentUser;
  private _subs = new Subscription();

  ngOnInit(): void {
    const sub = this._globalPropertiesContext.state$
      .pipe(
        map((properties) => properties.hostedEnabled),
        tap((isHosted) => {
          this._isHosted = isHosted;
        }),
        switchMap((isHosted) =>
          combineLatest([
            this._customUiOptionsContext.state$,
            this._systemStatusContext.state$,
            this._authenticationContext.state$,
            isHosted ? this._tenantsStateContext.cachedTenantState$.pipe(take(1)) : of(null),
          ]),
        ),
        tap(([uiOptions, systemStatus, currentUser, cachedTenantState]) => {
          this._endpointDisabled = systemStatus.endpointDisabled;
          this._reportingEnabled = uiOptions.reportingEnabled;
          this._currentUser = currentUser;

          if (this._isHosted && cachedTenantState) {
            this.cachedTenantId = cachedTenantState.tenantId;
            this._setHostedNav(cachedTenantState, currentUser);
            this._checkForDialPlans();
          } else {
            this._setOnPremNav();
          }

          this.navProps.update((p) => ({
            ...p,
            endpointDisabled: this._endpointDisabled,
            isMsDialPlanConfigured: this._isHosted ? this.isDialPlansConfigured : true,
          }));

          this._checkReportLicense();
        }),
      )
      .subscribe();

    const routerSub = this._router.events.subscribe((route) => {
      if (route instanceof NavigationEnd) {
        this._checkReportLicense();
      }
    });

    this._subs.add(routerSub);
    this._subs.add(sub);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private _setOnPremNav(): void {
    this.navSections.set([
      {
        icon: ZiroIcons.REPORTS,
        dataAutomation: 'reporting-side-nav-section',
        title: 'tkey;home.top.nav.cisco',
        links: buildZpcReportLinks(),
      },
    ]);
  }

  private _buildZpmReportLinks(tenantId: number, currentUser: CurrentUser): NavSectionLink[] {
    const tenantRelativePath = (segment: string): string => `tenants/${tenantId}/${segment}`;

    const entries: {
      path: string;
      tkey: string;
      dataAutomation: string;
      isBeta?: boolean;
      icon: ZiroIcons;
      isVisible: () => boolean;
    }[] = [
      {
        path: tenantRelativePath('audit-logs'),
        tkey: 'tkey;reports.microsoft.audits.logs.title',
        dataAutomation: 'reporting-microsoft-audit-logs-link',
        icon: ZiroIcons.AUDITS,
        isVisible: () =>
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.AUDIT_REPORTS, this.cachedTenantId),
      },
      {
        path: tenantRelativePath('pstn-call-record-finder'),
        tkey: 'tkey;reports.microsoft.pstn_call_record_finder.title',
        dataAutomation: 'reporting-microsoft-pstn-call-record-finder-link',
        isBeta: true,
        icon: ZiroIcons.SEARCH,
        isVisible: () =>
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.CDR_REPORTS, this.cachedTenantId),
      },
      {
        path: tenantRelativePath('teams-pstn-calling-usage'),
        tkey: 'tkey;reports.microsoft.teams_pstn_calling_usage.title',
        dataAutomation: 'reporting-microsoft-teams-pstn-calling-usage-link',
        icon: ZiroIcons.PHONE,
        isVisible: () =>
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.CDR_REPORTS, this.cachedTenantId),
      },
      {
        path: tenantRelativePath('policy-misconfigurations'),
        tkey: 'tkey;reports.microsoft.policy_misconfig.title',
        dataAutomation: 'reporting-microsoft-policy-misconfigurations-link',
        icon: ZiroIcons.WRENCH,
        isVisible: () =>
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.CONFIG_REPORTS, this.cachedTenantId),
      },
      {
        path: tenantRelativePath('misconfigured-voice-apps'),
        tkey: 'tkey;reports.microsoft.misconfigured_voice_apps.title',
        dataAutomation: 'reporting-microsoft-misconfigured-voice-apps-link',
        isBeta: true,
        icon: ZiroIcons.WRENCH,
        isVisible: () => false,
      },
      {
        path: tenantRelativePath('dial-plan-inventory'),
        tkey: 'tkey;reports.microsoft.dial_plan_inventory.title',
        dataAutomation: 'reporting-microsoft-dial-plan-inventory-link',
        icon: ZiroIcons.PHONE_DIAL,
        isVisible: () =>
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.CONFIG_REPORTS, this.cachedTenantId),
      },
      {
        path: tenantRelativePath('custom-powershell-queries'),
        tkey: 'tkey;reports.microsoft.custom_powershell_reports.title',
        dataAutomation: 'reporting-microsoft-custom-powershell-queries-link',
        icon: ZiroIcons.POWERSHELL,
        isVisible: () =>
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.CONFIG_REPORTS, this.cachedTenantId),
      },
    ];

    return entries.map(
      (row): NavSectionLink => ({
        link: row.path,
        text: row.tkey,
        dataAutomation: row.dataAutomation,
        icon: row.icon,
        isRouterLink: true,
        isBeta: row.isBeta,
        isVisible: row.isVisible,
        isDisabled: hostedLinkDisabled(row.path),
        disabledTooltip: hostedDialPlanTooltipFor(row.path),
      }),
    );
  }

  private _setHostedNav(cachedTenantState: PrivilegedTenant, currentUser: CurrentUser): void {
    this.navSections.set([
      {
        icon: ZiroIcons.REPORTS,
        dataAutomation: 'reporting-side-nav-section',
        title: 'tkey;api.page.reports.navlink',
        hideSectionHeader: true,
        links: this._buildZpmReportLinks(cachedTenantState.tenantId, currentUser),
      },
    ]);
  }

  private _checkReportLicense(): void {
    this.pageDisabled =
      !this._reportingEnabled &&
      !this._router.url.endsWith('/orphaned-devices') &&
      !this._router.url.endsWith('/dashboard') &&
      !this._router.url.endsWith('/cucm-license-optimization') &&
      !this._router.url.endsWith('/unused-phone-button-templates');
  }

  private _checkForDialPlans(): void {
    if (
      this._isHosted &&
      this._authenticationContext.userHasPrivilege(this._currentUser, ZpmPrivilege.CONFIG_REPORTS, this.cachedTenantId)
    ) {
      const subscription = this._microsoftDialPlansFieldConfigContext.state$.subscribe((dialPlans) => {
        this.isDialPlansConfigured = dialPlans.length > 0;
        this.navProps.update((p) => ({ ...p, isMsDialPlanConfigured: this.isDialPlansConfigured }));
      });
      this._subs.add(subscription);
    }
  }
}
